import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, requestNotificationPermission } from '../config/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPreferences, setUserPreferences] = useState(null);

  // Create user document in Firestore
  const createUserDocument = async (user, additionalData = {}) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      const { email, displayName, photoURL } = user;
      const createdAt = new Date();

      try {
        await setDoc(userRef, {
          email,
          displayName,
          photoURL,
          createdAt,
          ...additionalData,
        });

        // Create default preferences
        const preferencesRef = doc(db, 'preferences', user.uid);
        await setDoc(preferencesRef, {
          rainTolerance: 30,
          windTolerance: 20,
          heatTolerance: 30,
          notifications: {
            email: true,
            push: false,
            advanceHours: 2,
          },
          defaultEventDuration: 2,
          theme: 'space',
        });

        // Request notification permission
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) {
          await setDoc(userRef, { fcmToken }, { merge: true });
        }
      } catch (error) {
        console.error('Error creating user document:', error);
      }
    }
  };

  // Fetch user preferences
  const fetchUserPreferences = async (userId) => {
    try {
      const preferencesRef = doc(db, 'preferences', userId);
      const snapshot = await getDoc(preferencesRef);
      if (snapshot.exists()) {
        setUserPreferences(snapshot.data());
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  // Register with email and password
  const register = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    await createUserDocument(result.user, { displayName });
    return result;
  };

  // Login with email and password
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Login with Google
  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    await createUserDocument(result.user);
    return result;
  };

  // Logout
  const logout = () => {
    return signOut(auth);
  };

  // Update user preferences
  const updateUserPreferences = async (preferences) => {
    if (!currentUser) return;

    try {
      const preferencesRef = doc(db, 'preferences', currentUser.uid);
      await setDoc(preferencesRef, preferences, { merge: true });
      setUserPreferences((prev) => ({ ...prev, ...preferences }));
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await createUserDocument(user);
        await fetchUserPreferences(user.uid);
      } else {
        setUserPreferences(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userPreferences,
    register,
    login,
    loginWithGoogle,
    logout,
    updateUserPreferences,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
